import React from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';

export const Footer = () => {
  const [email, setEmail] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { toast } = useToast();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('email_subscriptions')
        .insert([{ email, source: 'footer' }]);

      // 23505 = unique_violation (duplicate email) — treat as success
      if (error && error.code !== '23505') throw error;

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'email_signup', { source: 'footer' });
      }
      toast({
        title: "You're subscribed!",
        description: "We'll alert you to new deals and discounts.",
      });
      setEmail('');
    } catch (error) {
      console.error('Error subscribing:', error);
      toast({
        title: "Error",
        description: "Failed to subscribe. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand column */}
          <div className="col-span-1">
            <Link to="/" className="mb-4 inline-block">
              <img
                src="/lovable-uploads/cbcb9241-13c5-4453-b79d-961c777edd77.png"
                alt="Pinnacle Buy Logo"
                className="h-10 w-auto"
                style={{ filter: 'brightness(0) saturate(100%) invert(17%) sepia(89%) saturate(3136%) hue-rotate(208deg) brightness(95%) contrast(98%)' }}
              />
            </Link>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Independent comparison of futures prop trading firms. We verify pricing, rules, and promo codes so you don't have to.
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Built by traders, for traders.
            </p>
          </div>

          {/* Quick links */}
          <div className="col-span-1">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase mb-4">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/compare" className="text-gray-600 dark:text-gray-400 hover:text-brand-blue dark:hover:text-brand-blue">
                  Compare Firms
                </Link>
              </li>
              <li>
                <Link to="/methodology" className="text-gray-600 dark:text-gray-400 hover:text-brand-blue dark:hover:text-brand-blue">
                  Methodology
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-gray-600 dark:text-gray-400 hover:text-brand-blue dark:hover:text-brand-blue">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-600 dark:text-gray-400 hover:text-brand-blue dark:hover:text-brand-blue">
                  About
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="col-span-1">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase mb-4">Legal</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/privacy" className="text-gray-600 dark:text-gray-400 hover:text-brand-blue dark:hover:text-brand-blue">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-gray-600 dark:text-gray-400 hover:text-brand-blue dark:hover:text-brand-blue">
                  Terms of Service
                </Link>
              </li>
            </ul>
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase mb-2">Affiliate Disclosure</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                Some links are affiliate links. We may earn a commission at no extra cost to you. This does not influence our reviews.
              </p>
            </div>
          </div>

          {/* Newsletter */}
          <div className="col-span-1">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase mb-4">Get Trading Updates</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Subscribe to receive updates on the latest prop firm deals and promotions.
            </p>
            <form onSubmit={handleSubscribe} className="flex space-x-2">
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-grow dark:bg-gray-800 dark:border-gray-700"
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-brand-blue hover:bg-brand-darkBlue"
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <span className="animate-spin mr-2">○</span>
                    <span>...</span>
                  </span>
                ) : (
                  <Mail className="h-4 w-4" />
                )}
              </Button>
            </form>
          </div>
        </div>

        {/* Bottom section */}
        <div className="border-t border-gray-200 dark:border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-4 md:mb-0">
              © {new Date().getFullYear()} PinnacleBuy. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-gray-500 dark:text-gray-400 hover:text-brand-blue dark:hover:text-brand-blue text-sm">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-500 dark:text-gray-400 hover:text-brand-blue dark:hover:text-brand-blue text-sm">
                Terms of Service
              </Link>
              <Link to="/about" className="text-gray-500 dark:text-gray-400 hover:text-brand-blue dark:hover:text-brand-blue text-sm">
                About
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
